export type PricedBlock = {
  variant_price?: number | string | null;
  booking_minutes?: number | null;
  duration_minutes?: number | null;
};

export type PricedSegment = {
  blocks?: Array<PricedBlock> | null;
};

type SplitPrices = {
  includedTotal: number;
  hasIncluded: boolean;
  usesDuration: boolean;
  rate30: number | null;
  rate60: number | null;
};

function blockPrice(block?: PricedBlock | null): number | null {
  if (block?.variant_price == null || block.variant_price === "") {
    return null;
  }

  const price = Number(block.variant_price);
  return Number.isFinite(price) ? price : null;
}

function splitBlocks(blocks?: Array<PricedBlock> | null): SplitPrices | null {
  if (!blocks?.length) {
    return null;
  }

  let includedTotal = 0;
  let hasIncluded = false;
  let hasPrice = false;
  let rate30: number | null = null;
  let rate60: number | null = null;

  blocks.forEach(block => {
    const price = blockPrice(block);
    if (price == null) {
      return;
    }

    hasPrice = true;
    const minutes = Number(block.booking_minutes);
    if (minutes === 30) {
      if (rate30 == null) {
        rate30 = price;
      }
      return;
    }
    if (minutes === 60) {
      if (rate60 == null) {
        rate60 = price;
      }
      return;
    }

    includedTotal += price;
    hasIncluded = true;
  });

  if (!hasPrice) {
    return null;
  }

  return {
    includedTotal,
    hasIncluded,
    usesDuration: rate30 != null || rate60 != null,
    rate30,
    rate60,
  };
}

function includedAmount(split: SplitPrices): number | null {
  return split.hasIncluded ? split.includedTotal : null;
}

function priceForLine(split: SplitPrices, lineMinutes: number): number | null {
  if (!split.usesDuration) {
    return split.includedTotal;
  }
  if (lineMinutes === 30) {
    if (split.rate30 == null) {
      return includedAmount(split);
    }
    return split.rate30 + split.includedTotal;
  }
  if (lineMinutes >= 60) {
    if (split.rate60 == null) {
      return includedAmount(split);
    }
    return split.rate60 + split.includedTotal;
  }

  return null;
}

export function segmentAvailabilityPrice(blocks?: Array<PricedBlock> | null): {
  amount: number | null;
  from: boolean;
} {
  const split = splitBlocks(blocks);
  if (!split) {
    return { amount: null, from: false };
  }
  if (!split.usesDuration) {
    return { amount: split.includedTotal, from: false };
  }

  const options: Array<number> = [];
  if (split.rate30 != null) {
    options.push(split.rate30 + split.includedTotal);
  }
  if (split.rate60 != null) {
    options.push(split.rate60 + split.includedTotal);
  }
  if (options.length === 0) {
    return { amount: null, from: false };
  }

  return { amount: Math.min(...options), from: true };
}

function slotMinutes(segment?: PricedSegment | null): number {
  const minutes = Number(segment?.blocks?.[0]?.duration_minutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 30;
}

export function bookingLineAmount(
  segments: Array<PricedSegment> | null | undefined,
  segmentIndex: number,
  hourLinesOnly: boolean,
): number | null {
  const list = segments ?? [];
  const segment = list[segmentIndex];
  const split = splitBlocks(segment?.blocks);
  if (!split) {
    return null;
  }
  if (!split.usesDuration) {
    return split.includedTotal;
  }

  const totalMinutes = list.reduce((sum, item) => sum + slotMinutes(item), 0);
  const minutesBefore = hourLinesOnly
    ? (segmentIndex / 2) * 60
    : list.slice(0, segmentIndex).reduce((sum, item) => sum + slotMinutes(item), 0);
  const remaining = totalMinutes - minutesBefore;

  if (!hourLinesOnly && minutesBefore % 60 !== 0) {
    return null;
  }
  if (remaining >= 60) {
    return priceForLine(split, 60);
  }
  if (remaining > 0) {
    return priceForLine(split, remaining);
  }

  return null;
}

export function segmentHourlyPrice(blocks?: Array<PricedBlock> | null): number | null {
  return segmentAvailabilityPrice(blocks).amount;
}
